
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Camera, 
  Cpu, 
  Users, 
  ArrowRight,
  MapPin,
  Lock,
  Scissors,
  Hammer,
  Palette,
  Ruler,
  ChevronRight,
  Shield,
  Zap,
  Star,
  DollarSign,
  MessageSquare
} from 'lucide-react';

const MarketingPage: React.FC = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal');
          observerRef.current?.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
      observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="bg-[#faf9f6] min-h-screen text-[#1a1a1a]">
      {/* 1. Header & Hero Section */}
      <header className="fixed top-0 left-0 right-0 h-20 glass z-[100] border-b border-black/5">
        <div className="container mx-auto max-w-6xl px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2 group">
            <div className="bg-stone-900 p-2 rounded-lg text-amber-500 shadow-xl group-hover:rotate-12 transition-transform">
              <Hammer size={20} strokeWidth={2.5} />
            </div>
            <span className="font-black text-2xl tracking-tighter font-serif italic">SkillLink</span>
          </div>
          <nav className="hidden md:flex items-center gap-10 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400">
            <a href="#how-it-works" className="hover:text-amber-700 transition-colors">How it works</a>
            <a href="#use-cases" className="hover:text-amber-700 transition-colors">Use Cases</a>
            <a href="#features" className="hover:text-amber-700 transition-colors">Why SkillLink</a>
          </nav>
          <Link 
            to="/start" 
            className="bg-stone-900 text-white px-6 py-3 rounded-full font-bold text-[11px] uppercase tracking-widest hover:bg-amber-700 transition-all shadow-xl"
          >
            Try the App Now
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-48 pb-24 overflow-hidden">
        <div className="container mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-10 reveal-on-scroll">
            <h1 className="text-6xl lg:text-7xl font-black tracking-tight leading-[1.1] font-serif">
              Find the Right Artisan for <span className="italic text-amber-700 font-normal underline decoration-stone-200">Any Task</span> — Anytime, Anywhere
            </h1>
            <p className="text-xl text-stone-500 font-medium leading-relaxed max-w-lg">
              Post a request with a photo or video, get the perfect artisan to fix, create, or deliver — locally or remotely.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <Link 
                to="/start" 
                className="w-full sm:w-auto bg-stone-900 text-white px-12 py-6 rounded-2xl font-bold text-lg shadow-2xl hover:bg-amber-700 hover:-translate-y-1 transition-all flex items-center justify-center gap-4 group"
              >
                Try the App Now
                <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="#how-it-works" className="text-xs font-bold text-stone-400 hover:text-stone-900 transition-colors uppercase tracking-[0.2em]">
                Learn More
              </a>
            </div>
          </div>
          
          {/* App Illustration / Mockup */}
          <div className="relative reveal-on-scroll hidden md:block">
            <div className="absolute inset-0 bg-amber-100 rounded-[3rem] -rotate-3 scale-105 opacity-20"></div>
            <div className="bg-white p-8 rounded-[3rem] shadow-2xl border border-black/5 relative z-10 hover:scale-[1.02] transition-transform duration-700">
               <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center text-rose-700">
                        <Scissors size={20} />
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-stone-400">Task: Tailoring</span>
                    </div>
                  </div>
                  <div className="aspect-video bg-stone-50 rounded-2xl overflow-hidden relative border border-stone-100 group">
                    <img 
                      src="https://images.unsplash.com/photo-1594932224828-b4b059b6f6f2?auto=format&fit=crop&q=80&w=800" 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[5s]"
                      alt="Torn dress mockup"
                    />
                    <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur px-4 py-3 rounded-xl shadow-xl">
                      <p className="text-xs font-bold italic">"I ripped the zipper on my dress — need this fixed ASAP."</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 w-[65%] animate-shimmer"></div>
                    </div>
                    <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-stone-400">
                      <span>Analyzing Need...</span>
                      <span>AI Confidence: 98%</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {['Tailor', 'Locksmith', 'Hair', 'Carpenter'].map(cat => (
                      <span key={cat} className={`px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest border ${cat === 'Tailor' ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-300 border-stone-100'}`}>
                        {cat}
                      </span>
                    ))}
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. How It Works Section */}
      <section id="how-it-works" className="py-32 border-y border-black/5">
        <div className="container mx-auto max-w-6xl px-6 text-center">
          <h2 className="text-5xl font-black tracking-tight font-serif italic mb-20 text-balance">The SkillLink Workflow</h2>
          
          <div className="grid md:grid-cols-4 gap-12 text-left">
            <StepCard 
              num="01" 
              icon={<Camera />} 
              title="Post Your Need" 
              desc="Upload a photo, video, or describe your task — the app understands what you need." 
            />
            <StepCard 
              num="02" 
              icon={<Cpu />} 
              title="AI Categorization" 
              desc="The app automatically identifies the right artisan and asks any clarifying questions." 
            />
            <StepCard 
              num="03" 
              icon={<Users />} 
              title="Matches & Q/A" 
              desc="Relevant artisans show interest, ask questions, and suggest solutions publicly." 
            />
            <StepCard 
              num="04" 
              icon={<MapPin />} 
              title="Choose & Track" 
              desc="Pick your artisan, track their arrival or progress, and get your task completed safely." 
            />
          </div>
          
          <Link to="/start" className="inline-flex items-center gap-3 bg-stone-900 text-white px-10 py-5 rounded-2xl font-bold text-base mt-20 hover:bg-amber-700 transition-all group">
            Get Started <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
      </section>

      {/* 3. Use Cases / Examples Section */}
      <section id="use-cases" className="py-32 bg-stone-50">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="max-w-xl space-y-4 mb-20 reveal-on-scroll">
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-amber-700">Real Scenarios</span>
            <h2 className="text-6xl font-black tracking-tight font-serif italic">Everyday Problems, Solved Instantly.</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <CaseCard 
              title="Locksmithing" 
              desc="Locked out of your apartment? Find a nearby locksmith immediately." 
              img="https://images.unsplash.com/photo-1582139329536-e7284fece509?auto=format&fit=crop&q=80&w=400"
            />
            <CaseCard 
              title="Tailoring" 
              desc="Dress ripped last minute? A tailor comes to your location." 
              img="https://images.unsplash.com/photo-1524234107056-1c1f48f64ab8?auto=format&fit=crop&q=80&w=400"
            />
            <CaseCard 
              title="Woodworking" 
              desc="Custom furniture needed? Connect with a specialized carpenter anywhere." 
              img="https://images.unsplash.com/photo-1581781870027-04212e231e96?auto=format&fit=crop&q=80&w=400"
            />
            <CaseCard 
              title="Hair Stylist" 
              desc="New city haircut? Book a local stylist instantly." 
              img="https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&q=80&w=400"
            />
          </div>
        </div>
      </section>

      {/* 4. Features / Why SkillLink Section */}
      <section id="features" className="py-32">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="text-center space-y-4 mb-20 reveal-on-scroll">
            <h2 className="text-6xl font-black tracking-tight font-serif italic">Designed for Excellence</h2>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
            <FeatureTile 
              icon={<Star />} 
              title="Specialization Friendly" 
              desc="Artisans can focus on one skill or offer multiple services." 
            />
            <FeatureTile 
              icon={<Zap />} 
              title="On-Demand + Marketplace" 
              desc="Urgent requests like Uber; niche projects like Etsy." 
            />
            <FeatureTile 
              icon={<Shield />} 
              title="Safe & Secure" 
              desc="Only verified locations, users control address sharing." 
            />
            <FeatureTile 
              icon={<DollarSign />} 
              title="Transparent & Barter" 
              desc="Supports cash, barter, or platform credits seamlessly." 
            />
            <FeatureTile 
              icon={<MessageSquare />} 
              title="Knowledge Sharing" 
              desc="Q/A threads help future users with similar needs." 
            />
          </div>
        </div>
      </section>

      {/* 5. Social Proof / Testimonials Section */}
      <section className="py-32 bg-stone-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 wood-grain opacity-5 pointer-events-none"></div>
        <div className="container mx-auto max-w-4xl px-6 relative z-10">
          <div className="text-center mb-20 space-y-4 reveal-on-scroll">
            <h2 className="text-4xl font-black font-serif italic text-amber-500">Masterwork Feedback</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <Testimonial 
              quote="I got my dress fixed in 30 minutes thanks to SkillLink!" 
              author="Alex P." 
              role="Requester"
            />
            <Testimonial 
              quote="I love specializing in silk presses — SkillLink brings me clients who value my craft." 
              author="Jordan M." 
              role="Artisan"
            />
          </div>
        </div>
      </section>

      {/* 6. Call-to-Action Section */}
      <section className="py-32 px-6">
        <div className="container mx-auto max-w-5xl bg-stone-900 rounded-[3rem] p-20 text-center relative overflow-hidden group border border-amber-900/30">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-600/10 to-stone-900 group-hover:scale-110 transition-transform duration-[20s] linear"></div>
          <div className="relative z-10 space-y-10">
            <h2 className="text-6xl md:text-7xl font-black tracking-tight text-white font-serif italic">Ready to see it <br /> Handled?</h2>
            <p className="text-xl text-stone-400 max-w-xl mx-auto font-medium">No credit card needed — get started instantly and experience the era of local craft.</p>
            <Link 
              to="/start" 
              className="inline-flex items-center gap-4 bg-amber-500 text-stone-900 px-12 py-6 rounded-2xl font-black text-xl hover:bg-white transition-all shadow-2xl hover:scale-105"
            >
              Try SkillLink Now
              <ChevronRight size={24} />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. Footer */}
      <footer className="py-20 border-t border-black/5 text-stone-400">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-3 gap-16 items-center">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Hammer size={20} className="text-stone-900" />
                <span className="font-black text-xl tracking-tighter font-serif italic text-stone-900">SkillLink</span>
              </div>
              <p className="text-sm font-medium">The Right Artisan for Any Task.</p>
            </div>
            
            <div className="flex gap-10 justify-center text-[10px] font-bold uppercase tracking-widest">
              <a href="#" className="hover:text-amber-700 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-amber-700 transition-colors">Terms</a>
              <a href="#" className="hover:text-amber-700 transition-colors">Contact</a>
            </div>

            <div className="flex justify-end gap-6">
              {['Twitter', 'Instagram', 'LinkedIn'].map(social => (
                <a key={social} href="#" className="text-stone-300 hover:text-amber-700 transition-colors text-[10px] font-black uppercase tracking-[0.2em]">{social}</a>
              ))}
            </div>
          </div>
          <div className="mt-16 text-center opacity-30">
            <p className="text-[10px] font-bold uppercase tracking-[0.4em]">&copy; 2025 SkillLink Protocol</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Components for the Marketing Page
const StepCard = ({num, icon, title, desc}: {num: string, icon: React.ReactNode, title: string, desc: string}) => (
  <div className="space-y-6 reveal-on-scroll">
    <div className="flex items-end gap-3">
      <div className="text-4xl font-black text-amber-500 font-serif opacity-30">{num}</div>
      <div className="w-12 h-12 bg-white rounded-xl shadow-lg border border-stone-100 flex items-center justify-center text-amber-700 mb-1">
        {React.cloneElement(icon as React.ReactElement, { size: 24 })}
      </div>
    </div>
    <h3 className="text-xl font-black font-serif italic">{title}</h3>
    <p className="text-stone-500 text-sm font-medium leading-relaxed">{desc}</p>
  </div>
);

const CaseCard = ({title, desc, img}: {title: string, desc: string, img: string}) => (
  <div className="bg-white rounded-[2rem] overflow-hidden border border-black/5 soft-shadow hover:-translate-y-2 transition-transform duration-500 reveal-on-scroll">
    <div className="h-40 overflow-hidden relative group">
      <img src={img} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={title} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
    </div>
    <div className="p-6 space-y-3">
      <h3 className="text-lg font-black font-serif">{title}</h3>
      <p className="text-xs text-stone-500 leading-relaxed font-medium">{desc}</p>
    </div>
  </div>
);

const FeatureTile = ({icon, title, desc}: {icon: React.ReactNode, title: string, desc: string}) => (
  <div className="premium-card p-8 rounded-3xl flex flex-col items-center text-center space-y-4 hover:bg-amber-50 group reveal-on-scroll">
    <div className="text-stone-300 group-hover:text-amber-600 transition-colors">
      {React.cloneElement(icon as React.ReactElement, { size: 28 })}
    </div>
    <h3 className="text-sm font-black font-serif italic leading-tight">{title}</h3>
    <p className="text-[10px] text-stone-400 font-medium leading-relaxed">{desc}</p>
  </div>
);

const Testimonial = ({quote, author, role}: {quote: string, author: string, role: string}) => (
  <div className="space-y-6 reveal-on-scroll">
    <div className="text-amber-500 opacity-20">
      <MessageSquare size={40} fill="currentColor" />
    </div>
    <p className="text-2xl font-black font-serif italic text-amber-50 leading-snug">"{quote}"</p>
    <div>
      <div className="font-black text-amber-500 uppercase tracking-widest text-xs">{author}</div>
      <div className="text-[10px] text-stone-500 font-bold uppercase tracking-[0.4em] mt-1">{role}</div>
    </div>
  </div>
);

export default MarketingPage;
